/**
 * MH-20 Phát bản tin khẩn cấp (mục 6.4 đặc tả giao diện) — màn hình RIÊNG,
 * không phải một lựa chọn trong phát ngay, vì hệ quả và quy trình khác hẳn.
 *
 * Ba điều kiện bắt buộc trước khi nút THỰC HIỆN được kích hoạt: đã chọn nguồn
 * phát (bản tin đã duyệt), đã chọn phạm vi, và đã nhập ĐÚNG từ xác nhận
 * "KHANCAP". Bản kê hệ quả định lượng do máy chủ trả về
 * (`POST /api/v1/broadcast/emergency/impact`) hiển thị NGUYÊN VĂN bốn số liệu
 * bắt buộc của cổng nghiệm thu: số cụm loa, số cụm đang phát nội dung khác, số
 * lịch bị hủy, số cụm kết nối gián tiếp.
 *
 * Sau khi gửi lệnh, màn hình chuyển sang chế độ theo dõi trực tiếp — TÁI SỬ
 * DỤNG MH-23 (`MH23TheoDoiPhien`) thay vì viết một bảng theo dõi riêng, đây
 * cũng là màn hình DUY NHẤT được phép làm mới dưới 5 giây.
 */
import { useState, type ReactNode } from 'react';
import { dienVao, vi } from '../ngon-ngu/vi';
import { banTinApi, LOAI_BAN_TIN_CUM_LOA, type DongBanTin } from '../api/ban-tin-api';
import { phatSongApi, type BanKeHeQuaKhanCap } from '../api/phat-song-api';
import { dungTaiDuLieu } from '../trang-thai/dung-tai-du-lieu';
import { KhungXuong, TrangThaiLoi } from '../thanh-phan/TrangThaiManHinh';
import { BoChonPhamViDiaBan, nhanPhamVi } from '../thanh-phan/BoChonPhamViDiaBan';
import { dungPhien } from '../trang-thai/ngu-canh-phien';
import { MH23TheoDoiPhien } from './MH23TheoDoiPhien';

const TU_XAC_NHAN = 'KHANCAP';

export interface MH20Props {
  api?: Pick<typeof phatSongApi, 'khaoSatHeQua' | 'phatKhanCap'>;
  banTinApiGoi?: Pick<typeof banTinApi, 'danhSach'>;
}

export function MH20PhatKhanCap({ api, banTinApiGoi }: MH20Props = {}): ReactNode {
  const goi = api ?? phatSongApi;
  const banTinGoi = banTinApiGoi ?? banTinApi;
  const phien = dungPhien();

  const [banTinId, datBanTinId] = useState('');
  // Tài khoản cấp xã: phạm vi mặc định là xã được phân công.
  const [phamVi, datPhamVi] = useState<string[]>(() => (phien.khoaDiaBan ? [phien.khoaDiaBan.ma] : []));
  const [moPhamVi, datMoPhamVi] = useState(false);
  const [tuNhap, datTuNhap] = useState('');
  const [dangGui, datDangGui] = useState(false);
  const [loi, datLoi] = useState<string | null>(null);
  const [phienId, datPhienId] = useState<string | null>(null);

  const banTinDuyet = dungTaiDuLieu<DongBanTin[]>({
    tai: async (signal) => {
      const kq = await banTinGoi.danhSach(
        { trangThai: 'da_duyet', loaiBanTin: LOAI_BAN_TIN_CUM_LOA, pageSize: 50 },
        signal,
      );
      return kq.items;
    },
    laRong: (d) => d.length === 0,
    dangLoc: false,
    phuThuoc: [],
  });

  const heQua = dungTaiDuLieu<BanKeHeQuaKhanCap>({
    tai: (_signal) => goi.khaoSatHeQua(phamVi.map((ma) => ({ DichID: ma }))),
    laRong: () => false,
    dangLoc: false,
    phuThuoc: [phamVi.join(',')],
    tat: phamVi.length === 0,
  });

  // Ba điều kiện bắt buộc: nguồn phát, phạm vi, từ xác nhận đúng.
  const batDuoc = Boolean(banTinId) && phamVi.length > 0 && tuNhap.trim().toUpperCase() === TU_XAC_NHAN;

  const thucHien = async (): Promise<void> => {
    datDangGui(true);
    datLoi(null);
    try {
      const kq = await goi.phatKhanCap({ banTinId, phamVi: phamVi.map((ma) => ({ DichID: ma })) });
      datPhienId((kq as { phienId?: string }).phienId ?? null);
    } catch (e: unknown) {
      datLoi(e instanceof Error ? e.message : 'Không gửi được lệnh phát khẩn cấp');
    } finally {
      datDangGui(false);
    }
  };

  if (!phien.coQuyen('broadcast.emergency')) {
    return <p role="alert">{vi.phatSong.khanCapChiDanhChoQuyen}</p>;
  }

  // Sau khi gửi lệnh: chuyển hẳn sang chế độ theo dõi trực tiếp của MH-23.
  if (phienId) {
    return (
      <div className="man-hinh man-hinh--khan-cap" data-man-hinh="MH-20" data-che-do="theo-doi">
        <p role="status">{vi.phatSong.daChuyenSangTheoDoi}</p>
        <MH23TheoDoiPhien phienId={phienId} chuKyMs={2_000} />
      </div>
    );
  }

  return (
    <div className="man-hinh man-hinh--khan-cap" data-man-hinh="MH-20" data-che-do="thiet-lap">
      <h1>{vi.phatSong.khanCapTieuDe}</h1>

      <fieldset>
        <legend>{vi.phatSong.nguonPhat}</legend>
        <select
          value={banTinId}
          onChange={(e) => datBanTinId(e.target.value)}
          aria-label={vi.phatSong.nguonBanTinDaDuyet}
        >
          <option value="">—</option>
          {(banTinDuyet.duLieu ?? []).map((b) => (
            <option key={b.id} value={b.id}>
              {b.tieuDe}
            </option>
          ))}
        </select>
      </fieldset>

      <span id="mh20-pham-vi-nhan">{vi.bienTap.nhanPhamVi}</span>
      <p data-vung="pham-vi-hien-tai">
        {phamVi.length === 0 ? vi.bienTap.chuaChonPhamVi : nhanPhamVi(phamVi, phien.tenDiaBanTheoMa)}
      </p>
      <button
        type="button"
        className="nut"
        aria-describedby="mh20-pham-vi-nhan"
        onClick={() => datMoPhamVi(true)}
      >
        {vi.bienTap.chonPhamVi}
      </button>

      <section aria-label={vi.phatSong.heQuaTieuDe} data-vung="he-qua">
        <h2>{vi.phatSong.heQuaTieuDe}</h2>
        {phamVi.length === 0 ? null : heQua.trangThai === 'dang-tai' ? (
          <KhungXuong soDong={4} soCot={1} taiLau={heQua.taiLau} />
        ) : heQua.trangThai === 'loi' && heQua.loi ? (
          <TrangThaiLoi loi={heQua.loi} onThuLai={heQua.taiLai} />
        ) : heQua.duLieu ? (
          <ul>
            <li data-vung="so-cum-loa-trong-pham-vi">
              {dienVao(vi.phatSong.heQuaPhatToi, {
                so: heQua.duLieu.soCumLoaTrongPhamVi,
                soXa: phamVi.length,
              })}
            </li>
            <li data-vung="so-cum-dang-phat-noi-dung-khac">
              {dienVao(vi.phatSong.heQuaChiemQuyen, { so: heQua.duLieu.soCumDangPhatNoiDungKhac })}
            </li>
            <li data-vung="so-lich-se-bi-huy">
              {dienVao(vi.phatSong.heQuaHuyLich, { so: heQua.duLieu.soLichPhatSeBiHuy })}
            </li>
            <li data-vung="so-cum-ket-noi-gian-tiep">
              {dienVao(vi.phatSong.heQuaGianTiep, { so: heQua.duLieu.soCumKetNoiGianTiep })}
            </li>
          </ul>
        ) : null}
      </section>

      <label htmlFor="mh20-tu-xac-nhan">{dienVao(vi.phatSong.nhapTuXacNhan, { tu: TU_XAC_NHAN })}</label>
      <input
        id="mh20-tu-xac-nhan"
        value={tuNhap}
        onChange={(e) => datTuNhap(e.target.value)}
        autoComplete="off"
        aria-describedby="mh20-dieu-kien"
      />
      <p id="mh20-dieu-kien" className="chi-cho-doc-man-hinh">
        Nút thực hiện chỉ bật khi đã chọn nguồn phát, đã chọn phạm vi, và nhập đúng {TU_XAC_NHAN}
      </p>

      {loi ? (
        <p role="alert" data-vung="loi-phat-khan-cap">
          {loi}
        </p>
      ) : null}

      <div className="man-hinh__hanh-dong">
        <button type="button" className="nut">
          {vi.phatSong.huy}
        </button>
        <button
          type="button"
          className="nut nut--nguy-hiem"
          data-hanh-dong="thuc-hien-khan-cap"
          disabled={!batDuoc || dangGui}
          onClick={() => void thucHien()}
        >
          {vi.phatSong.thucHien}
        </button>
      </div>

      {moPhamVi ? (
        <BoChonPhamViDiaBan
          giaTri={phamVi}
          onXacNhan={(ds) => {
            datPhamVi(ds);
            datMoPhamVi(false);
          }}
          onHuy={() => datMoPhamVi(false)}
        />
      ) : null}
    </div>
  );
}
